import QuotationData from "@/quotation-data.json";
import DeliveryData from "@/delivery-data.json";
import { notify } from "@kyvg/vue3-notification";
import { GetStrategies } from "@/controllers/calculator.utilities";
import { ItemCalculator } from "@/controllers/item-calculator";
import { SquareMeterCalculationStrategy } from "@/controllers/strategies/sqm.strategy";
import { SingleCalculationStrategy } from "@/controllers/strategies/single.strategy";
import { CubicCalculationStrategy } from "@/controllers/strategies/cubic.strategy";
import { createStore } from "vuex";

function cloneQuotationData() {
	return JSON.parse(JSON.stringify(QuotationData));
}

function getPricelistEndpoint() {
	const el = document.getElementById("mva-bricks-data-inject");
	if (el) {
		const endpoint = el.getAttribute("data-pricelist-endpoint");
		if (endpoint) return endpoint;
	}
	return "/wp-json/mvabricks/v1/pricelist";
}

function mergeDbPricesIntoQuotationData(catalog, pricesByVariation) {
	if (!pricesByVariation || typeof pricesByVariation !== "object") {
		return catalog;
	}

	const merged = JSON.parse(JSON.stringify(catalog));

	for (const category of merged) {
		for (const product of category.products) {
			for (const variation of product.variations) {
				const key = `${product.id}::${variation.id}`;
				const mapped = pricesByVariation[key];
				if (!mapped) continue;

				if (mapped.private_amount != null) {
					variation.price.private_amount = mapped.private_amount;
				}
				if (mapped.contractor_amount != null) {
					variation.price.contractor_amount = mapped.contractor_amount;
				}
			}
		}
	}

	return merged;
}

const store = createStore({
	state: {
		quotationData: cloneQuotationData(),
		cart: [],
		isAddModalOpen: false,
		addItemModalData: {
			type: null,
			product: null,
			variation: null,
			quantityType: "single",
			quantity: 1,
		},
		isEditing: false,
		editIndex: -1,
		availableQuantities: [],
		cartUpdated: "",
		isCalculating: false,
		customerType: null,
		contractorDetails: {
			vatNumber: "",
			registrationNumber: "",
		},
		contactDetails: {
			name: "",
			number: "",
			email: "",
			receivingType: "collection",
			message: "",
		},
		deliveryDetails: null,
		calculatedResults: {
			products: [],
		},
		honeypot: "",
		exceedsTruckLoads: false,
	},
	getters: {
		isAddModalOpen() {
			return this.$store.state.isAddModalOpen;
		},
		quotationData: (state) => {
			return state.quotationData;
		},
		vatRate: (state) => {
			if (process.env.NODE_ENV === "development") return 15;
			const el = document.getElementById("mva-bricks-data-inject");
			if (!el) return 15;
			const rate = parseFloat(el.getAttribute("data-vat-rate"));
			return isNaN(rate) ? 15 : rate;
		},
		deliveryRates: (state) => {
			// return null;
			const deliveryRatesInject = document
				.getElementById("mva-bricks-data-inject")
				.getAttribute("data-delivery-rates");
			if (!deliveryRatesInject && process.env.NODE_ENV !== "development")
				return null;
			if (!deliveryRatesInject && process.env.NODE_ENV === "development") {
				return DeliveryData;
			}
			const deliveryAsObject = JSON.parse(deliveryRatesInject);
			if (deliveryRatesInject && deliveryAsObject) {
				const rates = [];
				for (const key of Object.keys(deliveryAsObject)) {
					rates.push(deliveryAsObject[key]);
				}
				return rates;
			}
			return null;
		},
		getProductTypes: (state) => {
			return state.quotationData.map((category) => {
				return {
					name: category.category_name,
					id: category.category_id,
					variation_label: category.variation_label,
				};
			});
		},
		getProducts: (state) => {
			if (!state.addItemModalData.type) return [];
			const products = state.quotationData.filter(
				(category) => category.category_id === state.addItemModalData.type.id,
			)[0].products;
			return products;
		},
		getProductVariations: (state) => {
			if (!state.addItemModalData.type || !state.addItemModalData.product)
				return [];
			const products = state.quotationData.filter(
				(category) => category.category_id === state.addItemModalData.type.id,
			)[0].products;
			const variations = products.filter(
				(product) => product.id === state.addItemModalData.product.id,
			)[0].variations;
			return variations;
		},
		calculationTotalResults: (state, getters) => {
			if (
				!state.calculatedResults ||
				!state.calculatedResults.products
			)
				return null;
			const vatMultiplier = 1 + getters.vatRate / 100;
			let products_price_excl_vat_subtotal = 0.0;
			let products_price_incl_vat_subtotal = 0.0;
			let products_price_vat_subtotal = 0.0;
			let palettes_total = 0;

			let shipping_excl_vat = 0.0;
			let shipping_incl_vat = 0.0;
			let shipping_vat = 0.0;

			for (const itemResult of state.calculatedResults.products) {
				if (itemResult.price_excl_vat)
					products_price_excl_vat_subtotal += itemResult.price_excl_vat;
				if (itemResult.price_incl_vat)
					products_price_incl_vat_subtotal += itemResult.price_incl_vat;
				if (itemResult.price_vat)
					products_price_vat_subtotal += itemResult.price_vat;
				if (itemResult.palettes) palettes_total += itemResult.palettes;
			}

			if (
				state.calculatedResults.idealRate &&
				state.contactDetails.receivingType === "delivery"
			) {
				if (palettes_total >= 0 && palettes_total <= 10) {
					// Small truck
					shipping_excl_vat = state.calculatedResults.idealRate.rate_small;
					shipping_incl_vat = shipping_excl_vat * vatMultiplier;
					shipping_vat = shipping_incl_vat - shipping_excl_vat;
				} else if (palettes_total > 10 && palettes_total <= 25) {
					// Large truck
					shipping_excl_vat = state.calculatedResults.idealRate.rate_large;
					shipping_incl_vat = shipping_excl_vat * vatMultiplier;
					shipping_vat = shipping_incl_vat - shipping_excl_vat;
				} else {
					shipping_excl_vat = 0;
					shipping_incl_vat = 0;
					shipping_vat = 0;
				}
			}

			const total_excl_vat =
				products_price_excl_vat_subtotal + shipping_excl_vat;
			const total_incl_vat =
				products_price_incl_vat_subtotal + shipping_incl_vat;
			const total_vat = products_price_vat_subtotal + shipping_vat;

			let additionalDeliveryData = {
				withinRange: null,
				distance: null,
			};
			if (state.contactDetails.receivingType === "delivery" && state.deliveryDetails?.distance?.value) {
				const deliveryDistanceMeters = state.deliveryDetails.distance.value;
				const deliveryDistanceKilometers = deliveryDistanceMeters * 0.001;
				additionalDeliveryData = {
					...additionalDeliveryData,
					withinRange: deliveryDistanceKilometers <= 250,
					distance: deliveryDistanceKilometers,
				};
			}

			return {
				products_price_excl_vat_subtotal,
				products_price_incl_vat_subtotal,
				products_price_vat_subtotal,
				palettes_total,
				receivingType: state.contactDetails.receivingType,
				shipping_excl_vat,
				shipping_incl_vat,
				shipping_vat,
				total_excl_vat,
				total_incl_vat,
				total_vat,
				vatRate: getters.vatRate,
				customerType: state.customerType,
				...additionalDeliveryData,
			};
		},
	},
	mutations: {
		setQuotationData(state, quotationData) {
			state.quotationData = quotationData;
		},
		showAddModal(state) {
			state.isAddModalOpen = true;
		},
		hideAddModal(state) {
			state.isAddModalOpen = false;
			state.addItemModalData = {
				type: null,
				product: null,
				variation: null,
				quantityType: "single",
				quantity: 1,
			};
			if (state.isEditing) {
				state.isEditing = false;
				state.editIndex = -1;
			}
		},
		removeCartItem(state, index) {
			state.cart = state.cart.filter((v, i) => {
				return i !== index;
			});
		},
		updateContactDetails(state, property) {
			const oldContactDetails = state.contactDetails;
			state.contactDetails = { ...state.contactDetails, ...property };
		},
		updateDeliveryDetails(state, deliveryDetails) {
			state.deliveryDetails = deliveryDetails;
		},
		updateAddModalData(state, property) {
			const oldStateModelData = Object.assign({}, state.addItemModalData);

			// state.addItemModalData = { ...state.addItemModalData, ...property };
			state.addItemModalData = Object.assign(state.addItemModalData, property);

			if (state.addItemModalData.type) {
				state.availableQuantities = GetStrategies(
					state.addItemModalData.type.id,
				);
				// if (state.availableQuantities.indexOf("sqm") > -1) {
				//     state.addItemModalData.quantityType = 'sqm';
				// } else {
				//     state.addItemModalData.quantityType = 'single';
				// }
			} else {
				state.availableQuantities = [];
				state.addItemModalData.quantityType = "single";
			}

			if (
				!state.addItemModalData.type ||
				oldStateModelData.type !== state.addItemModalData.type
			) {
				state.addItemModalData.product = null;
				state.addItemModalData.variation = null;
				state.quantityType = "single";
				return;
			}

			if (
				!state.addItemModalData.product ||
				oldStateModelData.product !== state.addItemModalData.product
			) {
				state.addItemModalData.variation = null;
			}

			if (
				state.addItemModalData.product &&
				!state.addItemModalData.variation &&
				state.addItemModalData.product.variations.length === 1
			) {
				state.addItemModalData.variation =
					state.addItemModalData.product.variations[0];
			}
		},
		enableEditing(state, index) {
			state.isEditing = true;
			state.editIndex = index;
			state.addItemModalData = { ...state.cart[index] };
		},
		updateCart(state) {
			let hasValidationPassed = true;

			if (!state.addItemModalData.type) {
				notify({
					group: "main",
					text: "You need to select a product type",
					type: "error",
				});
				hasValidationPassed = false;
			}

			if (!state.addItemModalData.product) {
				notify({
					group: "main",
					text: "You need to select a product",
					type: "error",
				});
				hasValidationPassed = false;
			}

			if (!state.addItemModalData.variation) {
				notify({
					group: "main",
					text: "You need to select the product variation",
					type: "error",
				});
				hasValidationPassed = false;
			}

			if (
				!state.addItemModalData.quantity ||
				state.addItemModalData.quantity < 0
			) {
				notify({
					group: "main",
					text: "Quantity must be larger than 0",
					type: "error",
				});
				hasValidationPassed = false;
			}

			if (
				state.addItemModalData.quantity % 1 !== 0 &&
				state.addItemModalData.quantityType === "single"
			) {
				notify({
					group: "main",
					text: "Quantity must be a whole number for Single items",
					type: "error",
				});
				hasValidationPassed = false;
			}

			if (!hasValidationPassed) return;

			try {
				if (state.isEditing) {
					state.cart[state.editIndex] = { ...state.addItemModalData };
					state.isEditing = false;
					state.editIndex = -1;
					state.cartUpdated = new Date().toISOString();
				} else {
					state.cart.push({ ...state.addItemModalData });
				}
			} catch (error) {
				console.error("Error updating cart:", error);
			}

			state.addItemModalData = {
				type: null,
				product: null,
				variation: null,
				quantityType: "single",
				quantity: 1,
			};

			state.isAddModalOpen = false;
		},
		enableCalculatingMode(state) {
			state.isCalculating = true;
		},
		disableCalculatingMode(state) {
			state.isCalculating = false;
		},
		storeCalculatedResults(state, results) {
			state.calculatedResults = results;
		},
		setHoneypot(state, value) {
			state.honeypot = value;
		},
		updateCustomerType(state, type) {
			state.customerType = type;
		},
		updateContractorDetails(state, details) {
			state.contractorDetails = { ...state.contractorDetails, ...details };
		},
	},
	actions: {
		async loadPricelistFromApi(context) {
			try {
				const endpoint = getPricelistEndpoint();
				const response = await fetch(endpoint, {
					method: "GET",
					headers: {
						Accept: "application/json",
					},
				});
				if (!response.ok) return;

				const payload = await response.json();
				if (!payload || !payload.prices) return;

				const mergedCatalog = mergeDbPricesIntoQuotationData(
					context.state.quotationData,
					payload.prices,
				);
				context.commit("setQuotationData", mergedCatalog);
			} catch (error) {
				console.warn("Could not load DB-backed pricelist, using existing catalog data.", error);
			}
		},
		calculateFinalResults(context) {
			context.commit("enableCalculatingMode");
			const vatMultiplier = 1 + context.getters.vatRate / 100;
			let idealRate = null;
			let exceedsDistanceLimit = false;
			let exceedsPalletLimit = false;

			if (context.state.contactDetails.receivingType === "delivery") {
				const deliveryDistance = context.state.deliveryDetails?.distance?.value;
				if (deliveryDistance) {
					const deliveryRates = context.getters.deliveryRates;
					const deliveryDistanceKilometers = deliveryDistance * 0.001;

					if (deliveryDistanceKilometers <= 250) {
						const filteredRates = deliveryRates.filter((rate) => {
							return (
								deliveryDistanceKilometers > rate.$gt &&
								deliveryDistanceKilometers <= rate.$lte
							);
						});

						idealRate = filteredRates[0];
					} else {
						idealRate = null;
						exceedsDistanceLimit = true;
					}
				}
			}

			const itemCalculations = [];

			for (const item of context.state.cart) {
				let itemCalculator = null;
				if (item.quantityType === "sqm") {
					itemCalculator = new ItemCalculator(
						new SquareMeterCalculationStrategy(),
					);
				} else if (item.quantityType === "single") {
					itemCalculator = new ItemCalculator(new SingleCalculationStrategy());
				} else if (item.quantityType === "cubic") {
					itemCalculator = new ItemCalculator(new CubicCalculationStrategy());
				}

				const { price_excl_vat, price_incl_vat, price_vat, palettes } =
					itemCalculator.calculate({
						variation: item.variation,
						quantity: item.quantity,
						quantityType: item.quantityType,
						type: item.type,
						receivingType: context.state.contactDetails.receivingType,
						customerType: context.state.customerType,
						vatMultiplier,
					});

				itemCalculations.push({
					name: item.product.name,
					variation: item.variation.name,
					type: item.type.name,
					type_id: item.type.id,
					quantity: item.quantity,
					quantityType: item.quantityType,
					price_excl_vat: price_excl_vat,
					price_incl_vat: price_incl_vat,
					price_vat: price_vat,
					palettes: palettes,
					receivingType: context.state.contactDetails.receivingType,
				});
			}

			let palettes_total = 0;
			for (const itemResult of itemCalculations) {
				if (itemResult.palettes) palettes_total += itemResult.palettes;
			}
			if (palettes_total > 25) {
				exceedsPalletLimit = true;
			}

			if (exceedsDistanceLimit || exceedsPalletLimit) {
				context.commit("storeCalculatedResults", {
					products: itemCalculations,
					idealRate: null,
					exceedsDistanceLimit,
					exceedsPalletLimit,
				});
			} else {
				context.commit("storeCalculatedResults", {
					products: itemCalculations,
					idealRate: idealRate,
					exceedsDistanceLimit,
					exceedsPalletLimit,
				});
			}

			context.commit("disableCalculatingMode");
		},
	},
});

export default store;
